import React, { useState, useEffect } from 'react';
import { notificationAPI } from '../../services/api';
import '../../styles/AdminStyles.css';

function AdminNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [formData, setFormData] = useState({
    notification_text: '',
    is_active: true,
    send_sms: false,
  });

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      // Use getAllNotifications for admin to see both active and inactive
      const data = await notificationAPI.getAllNotifications();
      setNotifications(data);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      alert('שגיאה בטעינת הודעות');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (notification = null) => {
    if (notification) {
      setEditingNotification(notification);
      setFormData({
        notification_text: notification.notification_text,
        is_active: notification.is_active === 1 || notification.is_active === true,
        send_sms: false,
      });
    } else {
      setEditingNotification(null);
      setFormData({
        notification_text: '',
        is_active: true,
        send_sms: false,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingNotification(null);
    setFormData({
      notification_text: '',
      is_active: true,
      send_sms: false,
    });
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingNotification) {
        await notificationAPI.updateNotification(editingNotification.id, {
          notification_text: formData.notification_text,
          is_active: formData.is_active,
        });
        alert('ההודעה עודכנה בהצלחה');
      } else {
        const result = await notificationAPI.createNotification(
          formData.notification_text,
          formData.send_sms
        );

        let message = 'ההודעה נוצרה בהצלחה';
        if (formData.send_sms && result.sms_sent !== undefined) {
          message += `\n\nנשלחו ${result.sms_sent} הודעות SMS`;
          if (result.sms_failed > 0) {
            message += `\n${result.sms_failed} הודעות נכשלו`;
          }
        }

        alert(message);
      }

      handleCloseModal();
      fetchNotifications();
    } catch (error) {
      console.error('Error saving notification:', error);
      alert(error.response?.data?.error || 'שגיאה בשמירת ההודעה');
    }
  };

  const handleToggleActive = async (notification) => {
    try {
      await notificationAPI.updateNotification(notification.id, {
        is_active: !notification.is_active,
      });
      fetchNotifications();
    } catch (error) {
      console.error('Error toggling notification:', error);
      alert('שגיאה בעדכון ההודעה');
    }
  };

  const handleDeleteNotification = async (notificationId) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק את ההודעה?')) {
      return;
    }

    try {
      await notificationAPI.deleteNotification(notificationId);
      alert('ההודעה נמחקה בהצלחה');
      fetchNotifications();
    } catch (error) {
      console.error('Error deleting notification:', error);
      alert('שגיאה במחיקת ההודעה');
    }
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-spinner"></div>
        <p>טוען הודעות...</p>
      </div>
    );
  }

  return (
    <div className="admin-notifications">
      <div className="admin-header">
        <div>
          <h1>ניהול הודעות</h1>
          <p>הודעות אלו מוצגות למשתמשים באפליקציה</p>
        </div>
        <button
          className="admin-button-primary"
          onClick={() => handleOpenModal()}
        >
          ➕ הוסף הודעה חדשה
        </button>
      </div>

      {/* Notifications List */}
      <div className="admin-notifications-list">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className={`admin-notification-card ${
              !notification.is_active ? 'admin-notification-inactive' : ''
            }`}
          >
            <div className="admin-notification-header">
              <div className="admin-notification-status">
                {notification.is_active ? (
                  <span className="admin-status-badge admin-status-active">
                    ✓ פעילה
                  </span>
                ) : (
                  <span className="admin-status-badge admin-status-inactive">
                    ✕ לא פעילה
                  </span>
                )}
              </div>
              <div className="admin-notification-date">
                {new Date(notification.created_at).toLocaleDateString('he-IL')}
              </div>
            </div>

            <div className="admin-notification-text">
              {notification.notification_text}
            </div>

            <div className="admin-notification-actions">
              <button
                className={`admin-button-toggle ${
                  notification.is_active ? 'active' : ''
                }`}
                onClick={() => handleToggleActive(notification)}
                title={notification.is_active ? 'השבת' : 'הפעל'}
              >
                {notification.is_active ? '👁️ השבת' : '👁️ הפעל'}
              </button>
              <button
                className="admin-button-edit"
                onClick={() => handleOpenModal(notification)}
                title="ערוך"
              >
                ✏️ ערוך
              </button>
              <button
                className="admin-button-delete"
                onClick={() => handleDeleteNotification(notification.id)}
                title="מחק"
              >
                🗑️ מחק
              </button>
            </div>
          </div>
        ))}

        {notifications.length === 0 && (
          <div className="admin-empty-state">
            <p>אין הודעות במערכת</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="admin-modal-overlay" onClick={handleCloseModal}>
          <div
            className="admin-modal admin-modal-large"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="admin-modal-header">
              <h2>{editingNotification ? 'ערוך הודעה' : 'הודעה חדשה'}</h2>
              <button className="admin-modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit} className="admin-form">
              <div className="admin-form-group">
                <label>טקסט ההודעה *</label>
                <textarea
                  name="notification_text"
                  value={formData.notification_text}
                  onChange={handleInputChange}
                  rows="5"
                  placeholder="הזן את תוכן ההודעה שתוצג למשתמשים..."
                  required
                ></textarea>
              </div>

              {editingNotification && (
                <div className="admin-form-group">
                  <label className="admin-checkbox-label">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                    />
                    <span>הודעה פעילה (תוצג למשתמשים)</span>
                  </label>
                </div>
              )}

              {!editingNotification && (
                <div className="admin-form-group">
                  <label className="admin-checkbox-label">
                    <input
                      type="checkbox"
                      name="send_sms"
                      checked={formData.send_sms}
                      onChange={handleInputChange}
                    />
                    <span>📱 שלח גם SMS לכל המשתמשים הפעילים</span>
                  </label>
                  <small style={{ color: '#666', marginTop: '5px' }}>
                    הודעת SMS תישלח לכל המשתמשים עם מנוי פעיל
                  </small>
                </div>
              )}

              <div className="admin-info-box">
                💡 הודעות פעילות מוצגות למשתמשים בדף הפרופיל
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  className="admin-button-secondary"
                  onClick={handleCloseModal}
                >
                  ביטול
                </button>
                <button type="submit" className="admin-button-primary">
                  {editingNotification ? 'עדכן' : 'צור'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminNotifications;
